import React, { useEffect, useState } from 'react'
import Table from './components/Table'
import Button from './components/Button'
import Modal from './components/Modal'
import transactionStore from './stores/transaction'
import purchaseOrderStore from './stores/purchase_order'
import locationsStore from './stores/locations'
import { produce } from 'immer'
import { Text, TextArea, Radios, Select } from './components/Fields'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro' // <-- import styles to be used
import SortableHeader from './components/SortableHeader'
import numeral from 'numeral'

const PurchaseOrderRow = ({ transaction }) => {
  const [editing, setEditing] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [open, setOpen] = useState(false)
  const [minimized, setMinimized] = useState(true)
  const [transactionToEdit, setTransactionToEdit] = useState({})
  const [error, setError] = useState(false)
  // const [receiving, setReceiving] = useState(false)
  const [
    currentTransaction,
    setCurrentTransaction,
    addToTransaction, 
    updateTransaction, 
    deleteTransaction,
    clear,
  ] = transactionStore(state => [
    state.transactionToEdit,
    state.setTransactionToEdit,
    state.add, 
    state.update, 
    state.delete,
    state.clear,
  ])

  const faTrash = icon({ name: 'trash' })
  const pencil = icon({ name: 'pencil' })

  function cancel(e) {
    e.preventDefault()
    setOpen(false)
    setEditing(false)
  }

  function edit(e) {
    e.preventDefault()
    setTransactionToEdit({...transaction})
    setEditing(true)
  }

  function order(e) {
    e.preventDefault()
    setTransactionToEdit({...transaction, state: 'order'})
    setEditing(true)
  }

  async function save(e) {
    e.preventDefault()
    await updateTransaction({...transactionToEdit})
    setEditing(false)
  }

  async function remove(e) {
    e.preventDefault()
    await deleteTransaction(transaction)
    setRemoving(false)
  }

  function receive(e, shouldClear = false) {
    // setReceiving(false)
    setError(false)
    if (!shouldClear) {
      if(Object.keys(currentTransaction.items_attributes).length > 0) {
        return setError(true)
      }
    }
    setCurrentTransaction({
      ...currentTransaction,
      direction: 1,
      purchase_order_id: transaction.id,
      items_attributes: [],
      location_id: transaction.location_id,
    })
    transaction.items_attributes.forEach(item => {
      addToTransaction(item.product, item.quantity)
    })
  }  

  function update(field, value) {
    setTransactionToEdit(produce(draft => {
      draft[field] = value
    }))
  }

  function updateQuantity(id, value) {
    setTransactionToEdit(produce(draft => {
      const index = draft.items_attributes.findIndex(item => item.id === id)
      draft.items_attributes[index].quantity = value
    }))
  }

  const past_due = transaction.expected_on && new Date(transaction.expected_on) < new Date()

  const vendors = [...new Set(
    (transaction.items_attributes || [])
      .map(item => item.product?.vendor_title)
      .filter(Boolean)
  )]

  return (
    <Table.Row className={ transaction.closed ? '' : past_due ? 'bg-red-600 text-white' : 'bg-red-50' }>
      <Table.Td>{transaction.id}</Table.Td>
      <Table.Td>{transaction.po_number}</Table.Td>
      <Table.Td>{transaction.urgent ? 'Yes' : 'No'}</Table.Td>
      <Table.Td>{transaction.location_name}</Table.Td>
      <Table.Td>
        <ul className="list-disc pl-6">
          {transaction.items_attributes && transaction.items_attributes.map((item) =>
            <li key={item.id}>
              {item.quantity} - {item.title}
              <span className="opacity-75"> ({numeral(item.quantity * parseFloat(item.product?.cost ?? 0)).format('$0,0.00')})</span>
            </li>
          )}
        </ul>
      </Table.Td>
      <Table.Td>
        <ul className="list-disc pl-6">
          {vendors.map((vendor) =>
            <li key={vendor}>{vendor}</li>
          )}
        </ul>
      </Table.Td>
      <Table.Td className="text-right whitespace-nowrap">{numeral(transaction.total_cost).format('$0,0.00')}</Table.Td>
      <Table.Td>{transaction.expected_on}</Table.Td>
      <Table.Td>
        <ul className="list-disc pl-6">
          {transaction.purchase_order_transactions.map(transaction => 
            <li key={transaction.id}>{transaction.id}</li>
          )}
        </ul>
      </Table.Td>
      <Table.Td>
        {transaction.notes}
      </Table.Td>
      <Table.Td>
        <Button onClick={edit}>
          <FontAwesomeIcon icon={pencil} />
          Edit
        </Button>
        <Modal isOpen={editing} onClose={() => setEditing(false)}>
          <Modal.Header onClose={() => setEditing(false)}>
            Editing Purchase Order #{transaction.po_number} - {transaction.created_at}
          </Modal.Header>
          <Modal.Body>
            {transactionToEdit.state == 'request' && (
              <label className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  checked={!!transactionToEdit.urgent}
                  onChange={e => update('urgent', e.target.checked)}
                />
                <span>Urgent purchase request</span>
              </label>
            )}
            <Text 
              label="PO Number" 
              value={transactionToEdit.po_number ?? ''} 
              onChange={e => update('po_number', e.target.value)}
              groupClass="mb-4"
            />
            <Text 
              label="Expected On" 
              type="date"  
              value={transactionToEdit.expected_on ?? ''} 
              onChange={e => update('expected_on', e.target.value)}
              groupClass="mb-4"
            />
            <TextArea
              label="Notes"
              value={transactionToEdit.notes ?? ''}
              onChange={e => update('notes', e.target.value)}
              groupClass="mb-4"
            />
            <h3 className="font-bold text-xl mb-2">Items</h3>
            <div className="p-2 rounded-lg bg-gray-100 mb-4">
              {transactionToEdit.state != 'request' && transactionToEdit.items_attributes && transactionToEdit.items_attributes.map((item) => 
                <Text
                  key={item.id}
                  type="number"
                  label={item.title}
                  value={item.quantity}
                  onChange={e => updateQuantity(item.id, e.target.value)}
                  groupClass="mb-4 last:mb-0"
                  />
              )}
              {transactionToEdit.state == 'request' && transactionToEdit.items_attributes && transactionToEdit.items_attributes.map((item) => 
                <div key={item.id}>
                  <div className="text-bold">{item.title}</div>
                  <div className="flex gap-2">
                    <div className="grow">
                      <Text
                        type="number"
                        label="Requested"
                        value={item.requested_quantity}
                        groupClass="mb-4 last:mb-0"
                        className="bg-gray-50 cursor-not-allowed"
                        readonly
                      />
                    </div>
                    <div className="grow">
                      <Text
                        type="number"
                        label="Ordered"
                        value={item.quantity}
                        onChange={e => updateQuantity(item.id, e.target.value)}
                        groupClass="mb-4 last:mb-0"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex items-center justify-between">
              <a href="#" onClick={cancel}>
                Cancel
              </a>
              <Button type="success" onClick={save}>
                <FontAwesomeIcon icon={icon({name: 'save'})} />
                Save
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </Table.Td>
      <Table.Td>
        <Button type="danger" onClick={() => setRemoving(true)}>
          <FontAwesomeIcon icon={faTrash} />
          Remove
        </Button>
        <Modal isOpen={removing} onClose={() => setRemoving(false)}>
          <Modal.Header onClose={() => setRemoving(false)}>
            Are you sure?
          </Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to remove transaction #{transaction.id}? This action cannot be undone.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex items-center justify-between">
              <a href="#" onClick={() => setRemoving(false)}>
                Cancel
              </a>
              <Button type="danger" onClick={remove}>
                <FontAwesomeIcon icon={faTrash} />
                Remove
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </Table.Td>
      <Table.Td>
        {!transaction.closed &&
          <>
            {transaction.state == 'request'
              ? <>
                <Button onClick={order}>
                  <FontAwesomeIcon icon={icon({name: 'inbox-in'})} />
                  Order
                </Button>
              </>
              : <>
                <Button onClick={receive}>
                  <FontAwesomeIcon icon={icon({name: 'inbox-in'})} />
                  Receive
                </Button>
              </>
            }
            {error && 
              <Modal isOpen={error} onClose={() => setError(false)}>
                <Modal.Header onClose={() => setError(false)}>
                  Error
                </Modal.Header>
                <Modal.Body>
                  <p>
                    You already have items in an open transaction. Would you
                    like to clear that transaction?
                  </p>
                </Modal.Body>
                <Modal.Footer>
                  <div className="flex items-center justify-between gap-4">
                    <a href="#" onClick={() => setError(false)}>
                      Cancel
                    </a>
                    <Button type="success" onClick={(e) => receive(e, true)}>
                      Clear
                    </Button>
                  </div>
                </Modal.Footer>
              </Modal>
            }
          </>
        }
      </Table.Td>
    </Table.Row>
  )
}

export default function () {
  const [errors, setErrors] = useState({})
  const [
    filtered,
    fetchTransactions,
    filters,
    updateFilter,
    showAll,
    setShowAll,
    setSort,
    currentSortKey,
    sortDirection,
  ] = purchaseOrderStore(state => [
    state.filtered,
    state.fetch,
    state.filters,
    state.updateFilter,
    state.showAll,
    state.setShowAll,
    state.setSort,
    state.sortKey,
    state.sortDirection,
  ])
  const [
    locations,
    fetchLocations,
  ] = locationsStore((state) => [
    state.collection,
    state.fetch,
  ])
  useEffect(() => {
    fetchTransactions()
    fetchLocations()
  }, [])

  return (
    <>
      <div className="flex justify-between gap-4 mb-4 items-end">
        <h1 className="text-4xl">Purchase Orders</h1>
        <div className="flex justify-center gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="show-all"
              checked={!showAll}
              onChange={() => setShowAll(false)}
            />
            <span className="ml-2">Open</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="show-all"
              checked={showAll}
              onChange={() => setShowAll(true)}
            />
            <span className="ml-2">All</span>
          </label>
        </div>
      </div>
      <Table>
        <Table.Head>
          <Table.Row>
            <SortableHeader sortKey="id" sortType="integer" {...{setSort, currentSortKey, sortDirection}}>
              ID
            </SortableHeader>
            <SortableHeader sortKey="po_number" sortType="string" {...{setSort, currentSortKey, sortDirection}}>
             PO Number
            </SortableHeader>
            <SortableHeader sortKey="urgent" sortType="boolean" {...{setSort, currentSortKey, sortDirection}}>
              Urgent
            </SortableHeader>
            <SortableHeader sortKey="location_name" sortType="string" {...{setSort, currentSortKey, sortDirection}}>
              Location
            </SortableHeader>
            <Table.Th>Product</Table.Th>
            <Table.Th>Vendor</Table.Th>
            <SortableHeader sortKey="total_cost" sortType="number" {...{setSort, currentSortKey, sortDirection}}>
              Projected Cost
            </SortableHeader>
            <SortableHeader sortKey="expected_on" sortType="date" {...{setSort, currentSortKey, sortDirection}}>
              Expected
            </SortableHeader>
            <Table.Th>Transactions</Table.Th>
            <Table.Th>Notes</Table.Th>
            <Table.Th></Table.Th>
            <Table.Th></Table.Th>
            <Table.Th></Table.Th>
          </Table.Row>
          <Table.Row>
            <Table.Td>
              <Text
                type="number"
                value={filters.id ?? ''}
                onChange={e => updateFilter('id', e.target.value)}
              />
            </Table.Td>
            <Table.Td>
              <Text
                value={filters.po_number ?? ''}
                onChange={e => updateFilter('po_number', e.target.value)}
              />
            </Table.Td>
            <Table.Td></Table.Td>
            <Table.Td>
              <Select
                onChange={e => updateFilter('location_id', e.target.value)}
                value={filters.location_id ?? ''}
              >
                <option value="">Select Location</option>
                {locations.map(location => <option key={location.id} value={location.id}>{location.title}</option>)}
              </Select>
            </Table.Td>
            <Table.Td>
              <Text
                value={filters.product ?? ''}
                onChange={e => updateFilter('product', e.target.value)}
              />
            </Table.Td>
            <Table.Td>
              <Text
                value={filters.vendor ?? ''}
                onChange={e => updateFilter('vendor', e.target.value)}
              />
            </Table.Td>
            <Table.Td></Table.Td>
            <Table.Td>
              <Text
                value={filters.expected_on ?? ''}
                onChange={e => updateFilter('expected_on', e.target.value)}
              />
            </Table.Td>
            <Table.Td></Table.Td>
            <Table.Td>
              <Text
                value={filters.notes ?? ''}
                onChange={e => updateFilter('notes', e.target.value)}
              />
            </Table.Td>
            <Table.Td></Table.Td>
            <Table.Td></Table.Td>
            <Table.Td></Table.Td>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {filtered().map(transaction => (
            <PurchaseOrderRow key={transaction.id} transaction={transaction} />
          ))}
        </Table.Body>
      </Table>
    </>
  )
}
